import type { LucideIcon } from 'lucide-react'
import type { AdminRouterContext } from '@/lib/router-context'
import type { Location } from '@/lib/services/locations/locations.types'
import type {
  NotificationChannel,
  NotificationEvent,
} from '@/lib/services/notification-events/notification-events.types'
import type { NotificationRecipient } from '@/lib/services/notification-recipients/notification-recipients.types'
import { useRouteOrganizationIdParam } from '@/hooks/useRouteOrganizationId'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import DefaultPending from '@/components/DefaultPending'
import { InfoNotice, OrganizationContextNotice } from '@/components/patterns/info-notice'
import SaveBar from '@/components/forms/SaveBar'
import CreateActionButton from '@/components/patterns/create-action-button'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { notificationRecipientColumns } from '@/components/tables/notifications/columns'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { SelectableSurface } from '@/components/patterns/selectable-surface'
import { EmptyState } from '@/components/patterns/empty-state'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can, useAbility } from '@/lib/casl'
import { NotificationChannelType } from '@/lib/enums/NotificationChannelType'
import { getNotificationChannelLabel } from '@/lib/notification-channel-labels'
import {
  notificationEventQueries,
  useNotificationEvents,
} from '@/lib/services/notification-events/notification-events.query'
import { locationFilters, locationQueries } from '@/lib/services/locations/locations.query'
import {
  notificationRecipientQueries,
  useNotificationRecipients,
} from '@/lib/services/notification-recipients/notification-recipients.query'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, Bell, ChevronDown, Mail, MessageSquare, Phone, Printer } from 'lucide-react'
import {
  getEffectiveNotificationRecipientsById,
  getEnabledNotificationRecipientIds,
  getEnabledNotificationRecipients,
  getNotificationRecipientsByChannel,
  hasEffectiveNotificationRecipient,
  hasEffectiveNotificationRecipientForEvent,
  notificationEventSupportsChannel,
} from './-notification-event-subscriptions'
import { useNotificationEventSubscriptionsForm } from './-use-notification-event-subscriptions-form'

type NotificationFallbackLocation = Pick<Location, 'phone' | 'email'>
type NotificationFallbackContact = { description: string } | null
type NotificationSubscriptionActions = ReturnType<typeof useNotificationEventSubscriptionsForm>
type NotificationChannelRecipients = Partial<Record<NotificationChannel, NotificationRecipient[]>>
type NotificationRecipientDisplay = (recipient: NotificationRecipient) => string

type NotificationRecipientSelection = {
  eventId: number
  eventName: string
  eventRecipientIds: number[]
  getRecipientValue: NotificationRecipientDisplay
  recipients: NotificationRecipient[]
  onToggleRecipient: NotificationSubscriptionActions['toggleRecipient']
}

type NotificationEventRowProps = {
  event: NotificationEvent
  eventRecipientIds: number[]
  eventSupportsChannel: typeof notificationEventSupportsChannel
  index: number
  canEdit: boolean
  getChannelCheckedState: NotificationSubscriptionActions['getChannelCheckedState']
  getFallbackContactForChannel: (channel: NotificationChannel) => NotificationFallbackContact
  getRecipientValue: NotificationRecipientDisplay
  isPending: boolean
  recipientsByChannel: NotificationChannelRecipients
  showPrimaryFallbackIndicators: boolean
  toggleChannel: NotificationSubscriptionActions['toggleChannel']
  toggleRecipient: NotificationSubscriptionActions['toggleRecipient']
}

type NotificationChannelCellProps = NotificationRecipientSelection & {
  canEdit: boolean
  channel: NotificationChannel
  channelLabel: string
  checkedState: boolean | 'indeterminate'
  fallbackContact: NotificationFallbackContact
  isSupported: boolean
  isPending: boolean
  showTopBorder: boolean
  onToggleChannel: NotificationSubscriptionActions['toggleChannel']
}

type NotificationRecipientDropdownProps = NotificationRecipientSelection & {
  channelLabel: string
  isDisabled: boolean
}

type PrimaryLocationFallbackCellProps = NotificationRecipientSelection & {
  canEdit: boolean
  channelLabel: string
  contactDescription: string
  isPending: boolean
  showTopBorder: boolean
}

export type NotificationSettingsLoaderInput = {
  context: {
    ability: Pick<AdminRouterContext['ability'], 'can'>
    queryClient: Pick<AdminRouterContext['queryClient'], 'prefetchQuery'>
  }
  params: { orgId: string | number }
}

const preferredChannelOrder: NotificationChannel[] = [
  NotificationChannelType.Text.Value,
  NotificationChannelType.Phone.Value,
  NotificationChannelType.Email.Value,
  NotificationChannelType.Fax.Value,
]

const channelValues = Object.values(NotificationChannelType).map((channelType) => channelType.Value)

const channelOrder = [
  ...preferredChannelOrder.filter((channel) => channelValues.includes(channel)),
  ...channelValues.filter((channel) => !preferredChannelOrder.includes(channel)),
]

const fallbackLocationFilters = locationFilters().get()

const channelIconByName: Record<string, LucideIcon> = {
  MessageSquare,
  Phone,
  Mail,
  Printer,
}

const channelTypeByValue = Object.fromEntries(
  Object.values(NotificationChannelType).map((channelType) => [channelType.Value, channelType])
)

const channelMeta = channelOrder.map((channel) => {
  const channelType = channelTypeByValue[channel]
  return {
    channel,
    label: getNotificationChannelLabel(channel, channelType?.Name ?? channel),
    Icon: channelIconByName[channelType?.Icon ?? ''] ?? Bell,
  }
})

function getPrimaryFallbackLocation(locations: Location[] = []): Location | undefined {
  const activeLocations = locations.filter((location) => location?.is_active === true)

  return activeLocations.find((location) => location?.is_default === true) ?? activeLocations[0]
}

function getPrimaryFallbackContactLabel(
  location: NotificationFallbackLocation | null | undefined
): string {
  const contacts = []

  if (location?.email) {
    contacts.push('email')
  }

  if (location?.phone) {
    contacts.push('phone')
  }

  return contacts.join(' and ')
}

function getPrimaryFallbackContactForChannel(
  location: NotificationFallbackLocation | null | undefined,
  channel: NotificationChannel
): NotificationFallbackContact {
  if (channel === NotificationChannelType.Email.Value && location?.email) {
    return {
      description: "primary active location's email",
    }
  }

  const phoneChannels: NotificationChannel[] = [
    NotificationChannelType.Text.Value,
    NotificationChannelType.Phone.Value,
  ]
  if (phoneChannels.includes(channel) && location?.phone) {
    return {
      description: "primary active location's phone",
    }
  }

  return null
}

export function notificationSettingsLoader({
  context,
  params,
}: NotificationSettingsLoaderInput): void {
  const canViewRecipients = context.ability.can('list', 'notification-recipients')
  const canViewEvents = context.ability.can('list', 'notification-events')

  if (!canViewRecipients && !canViewEvents) throw notFound()

  if (canViewRecipients) {
    void context.queryClient.prefetchQuery(
      notificationRecipientQueries.index({ organizationId: params.orgId })
    )
  }

  if (canViewEvents) {
    void context.queryClient.prefetchQuery(
      notificationEventQueries.index({ organizationId: params.orgId })
    )
  }

  if (context.ability.can('list', 'locations')) {
    void context.queryClient.prefetchQuery(
      locationQueries.index({
        organizationId: params.orgId,
        filters: fallbackLocationFilters,
      })
    )
  }
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/notifications/')({
  component: NotificationRecipients,
  loader: notificationSettingsLoader,
})
export function NotificationRecipients() {
  const orgId = useRouteOrganizationIdParam()
  const ability = useAbility()
  const canViewNotificationRecipients = ability.can('list', 'notification-recipients')
  const canViewNotificationEvents = ability.can('list', 'notification-events')
  const { data: notificationRecipients = [], isPending: recipientsPending } =
    useNotificationRecipients({ enabled: canViewNotificationRecipients })
  const { data: notificationEvents = [], isPending: eventsPending } = useNotificationEvents({
    enabled: canViewNotificationEvents,
  })
  const canViewLocations = ability.can('list', 'locations')
  const { data: locations = [], isPending: locationsPending } = useQuery({
    ...locationQueries.index({
      organizationId: orgId,
      filters: fallbackLocationFilters,
    }),
    enabled: canViewLocations,
  })
  const path = usePathHelper()
  const navigate = useNavigate()
  const isNotificationsLoading =
    (canViewNotificationRecipients && recipientsPending) ||
    (canViewNotificationEvents && eventsPending)
  const primaryFallbackLocation = getPrimaryFallbackLocation(locations)
  const primaryFallbackContactLabel = getPrimaryFallbackContactLabel(primaryFallbackLocation)
  const showFallbackNotice =
    canViewLocations &&
    !isNotificationsLoading &&
    !locationsPending &&
    notificationEvents.some((event) => event.fallback_to_primary_location) &&
    primaryFallbackContactLabel &&
    !hasEffectiveNotificationRecipient(notificationEvents, notificationRecipients)
  const canShowPrimaryFallbackIndicators =
    canViewLocations && !locationsPending && Boolean(primaryFallbackContactLabel)

  return (
    <>
      <AdminPageHeader
        icon={Bell}
        title="Notifications"
        description="Match the Notification Events to the recipients that should receive them."
        className="mb-4"
        actions={
          <IfGlobalOrg show={false}>
            <Can do="create" a="notification-recipients">
              <CreateActionButton asChild>
                <Link {...path.notifications.create().link}>Add Notification Recipient</Link>
              </CreateActionButton>
            </Can>
          </IfGlobalOrg>
        }
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Please do not configure notification recipients for the OneStem global organization.
          </OrganizationContextNotice>
        }
      >
        <>
          {isNotificationsLoading ? (
            <DefaultPending className="mb-4 h-auto py-8" />
          ) : (
            <>
              {showFallbackNotice ? (
                <InfoNotice
                  title="Primary Location Backup"
                  tone="warning"
                  icon={<AlertTriangle />}
                  className="mb-4"
                >
                  All notification events are off for verified recipients. We will use your primary
                  location&apos;s {primaryFallbackContactLabel} as a backup so new shop activity is
                  not missed.
                </InfoNotice>
              ) : null}

              {canViewNotificationEvents ? (
                <NotificationEventSubscriptions
                  notificationEvents={notificationEvents}
                  notificationRecipients={notificationRecipients}
                  primaryFallbackLocation={primaryFallbackLocation}
                  showPrimaryFallbackIndicators={canShowPrimaryFallbackIndicators}
                />
              ) : null}
            </>
          )}

          {canViewNotificationRecipients ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  <Typography variant="h4">Notification Recipients</Typography>
                </CardTitle>
                <CardDescription>
                  Manage the phone numbers, email addresses, and fax numbers that can receive your
                  notifications.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {recipientsPending || notificationRecipients.length ? (
                  <IndexDataTable
                    mode="client"
                    pagination
                    data={notificationRecipients}
                    columns={notificationRecipientColumns}
                    isLoading={recipientsPending}
                    onRowClick={(recipient) => navigate(path.notifications.show(recipient.id).link)}
                  />
                ) : (
                  <div className="text-center py-12">
                    <Typography variant="supporting">No notification recipients found.</Typography>
                    <div className="mt-4">
                      <Link {...path.notifications.create().link}>
                        <Button variant="outline">
                          <Typography variant="label">
                            Add your first notification recipient
                          </Typography>
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}
        </>
      </IfGlobalOrg>
    </>
  )
}

type NotificationEventSubscriptionsProps = {
  notificationEvents: NotificationEvent[]
  notificationRecipients: NotificationRecipient[]
  primaryFallbackLocation?: NotificationFallbackLocation | null
  showPrimaryFallbackIndicators?: boolean
}

export function NotificationEventSubscriptions({
  notificationEvents,
  notificationRecipients,
  primaryFallbackLocation = null,
  showPrimaryFallbackIndicators = false,
}: NotificationEventSubscriptionsProps) {
  const ability = useAbility()
  const { formatPhone } = usePhoneFormatter()
  const canEdit = ability.can('edit', 'notification-events')
  const enabledRecipients = getEnabledNotificationRecipients(notificationRecipients)
  const enabledRecipientIds = getEnabledNotificationRecipientIds(notificationRecipients)
  const effectiveRecipientsById = getEffectiveNotificationRecipientsById(notificationRecipients)
  const recipientsByChannel = getNotificationRecipientsByChannel(enabledRecipients, channelOrder)
  const {
    eventRecipientIds,
    form,
    getChannelCheckedState,
    syncRecipientsMutation,
    toggleChannel,
    toggleRecipient,
  } = useNotificationEventSubscriptionsForm({
    enabledRecipientIds,
    notificationEvents,
    recipientsByChannel,
  })

  const getRecipientValue = (recipient: NotificationRecipient): string => {
    if (['phone', 'text', 'fax'].includes(recipient.channel)) {
      return formatPhone(recipient.value)
    }

    return recipient.value
  }
  return (
    <form.AppForm>
      <form.Form>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>
              <Typography variant="h4">Notification Events</Typography>
            </CardTitle>
            <CardDescription>
              Choose which recipients should receive each notification event.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {notificationEvents.length ? (
              <div className="overflow-x-auto rounded-lg border border-border/70">
                <div
                  className="grid min-w-[700px]"
                  style={{
                    gridTemplateColumns: `minmax(320px, 1fr) repeat(${channelOrder.length}, 92px)`,
                  }}
                >
                  <div className="border-b bg-secondary/70 px-5 py-3 font-medium text-secondary-foreground">
                    Notification Events
                  </div>
                  {channelMeta.map((channelConfig) => {
                    const { channel } = channelConfig

                    return (
                      <div
                        key={channel}
                        className="border-b bg-secondary/70 px-2 py-3 text-center text-secondary-foreground"
                      >
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-center text-xs font-medium">
                              <channelConfig.Icon className="size-4" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{channelConfig.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    )
                  })}

                  {notificationEvents.map((event, index) => {
                    const selectedRecipientIds = eventRecipientIds[event.id] ?? []
                    // Only events that actually fall back to the primary location show it as a backup.
                    const showPrimaryFallbackIndicatorsForEvent =
                      showPrimaryFallbackIndicators &&
                      event.fallback_to_primary_location &&
                      !hasEffectiveNotificationRecipientForEvent(
                        event,
                        selectedRecipientIds,
                        effectiveRecipientsById
                      )

                    return (
                      <NotificationEventRow
                        key={event.id}
                        event={event}
                        eventRecipientIds={selectedRecipientIds}
                        eventSupportsChannel={notificationEventSupportsChannel}
                        index={index}
                        canEdit={canEdit}
                        getChannelCheckedState={getChannelCheckedState}
                        getFallbackContactForChannel={(channel) =>
                          getPrimaryFallbackContactForChannel(primaryFallbackLocation, channel)
                        }
                        getRecipientValue={getRecipientValue}
                        isPending={syncRecipientsMutation.isPending}
                        recipientsByChannel={recipientsByChannel}
                        showPrimaryFallbackIndicators={showPrimaryFallbackIndicatorsForEvent}
                        toggleChannel={toggleChannel}
                        toggleRecipient={toggleRecipient}
                      />
                    )
                  })}
                </div>
              </div>
            ) : (
              <EmptyState size="compact" title="No notification events available." />
            )}
          </CardContent>
        </Card>
        {canEdit ? <SaveBar mutation={syncRecipientsMutation} /> : null}
      </form.Form>
    </form.AppForm>
  )
}

function NotificationEventRow({
  event,
  eventRecipientIds,
  eventSupportsChannel,
  index,
  canEdit,
  getChannelCheckedState,
  getFallbackContactForChannel,
  getRecipientValue,
  isPending,
  recipientsByChannel,
  showPrimaryFallbackIndicators,
  toggleChannel,
  toggleRecipient,
}: NotificationEventRowProps) {
  return (
    <>
      <div className={cn('px-5 py-5', index !== 0 && 'border-t')}>
        <div className="font-medium">{event.name}</div>
      </div>
      {channelMeta.map(({ channel, label }) => (
        <NotificationChannelCell
          key={`${event.id}-${channel}`}
          canEdit={canEdit}
          channel={channel}
          channelLabel={label}
          checkedState={getChannelCheckedState(event.id, channel)}
          eventId={event.id}
          eventName={event.name}
          eventRecipientIds={eventRecipientIds}
          fallbackContact={
            showPrimaryFallbackIndicators ? getFallbackContactForChannel(channel) : null
          }
          isSupported={eventSupportsChannel(event, channel)}
          getRecipientValue={getRecipientValue}
          isPending={isPending}
          recipients={recipientsByChannel[channel] ?? []}
          showTopBorder={index !== 0}
          onToggleChannel={toggleChannel}
          onToggleRecipient={toggleRecipient}
        />
      ))}
    </>
  )
}

function NotificationChannelCell({
  canEdit,
  channel,
  channelLabel,
  checkedState,
  eventId,
  eventName,
  eventRecipientIds,
  fallbackContact,
  isSupported,
  getRecipientValue,
  isPending,
  recipients,
  showTopBorder,
  onToggleChannel,
  onToggleRecipient,
}: NotificationChannelCellProps) {
  const disabled = !canEdit || isPending || recipients.length === 0 || !isSupported
  const showDropdown = isSupported && recipients.length > 1

  if (!isSupported) {
    return (
      <div className={cn('px-3 py-5', showTopBorder && 'border-t')}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="mt-1 flex items-center justify-center">
              <span className="inline-flex">
                <Checkbox checked={false} disabled className="pointer-events-none" tabIndex={-1} />
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{channelLabel} not supported for this event</p>
          </TooltipContent>
        </Tooltip>
      </div>
    )
  }

  if (fallbackContact) {
    return (
      <PrimaryLocationFallbackCell
        canEdit={canEdit}
        channelLabel={channelLabel}
        contactDescription={fallbackContact.description}
        eventId={eventId}
        eventName={eventName}
        eventRecipientIds={eventRecipientIds}
        getRecipientValue={getRecipientValue}
        isPending={isPending}
        recipients={recipients}
        showTopBorder={showTopBorder}
        onToggleRecipient={onToggleRecipient}
      />
    )
  }

  return (
    <div className={cn('px-3 py-5', showTopBorder && 'border-t')}>
      <div className={cn('flex items-center justify-center gap-1', !showDropdown && 'mt-1')}>
        <Checkbox
          checked={checkedState}
          aria-label={`${eventName} ${channelLabel}`}
          disabled={disabled}
          onCheckedChange={(checked) => onToggleChannel(eventId, channel, checked === true)}
        />
        {showDropdown ? (
          <NotificationRecipientDropdown
            channelLabel={channelLabel}
            eventId={eventId}
            eventName={eventName}
            eventRecipientIds={eventRecipientIds}
            getRecipientValue={getRecipientValue}
            isDisabled={disabled}
            recipients={recipients}
            onToggleRecipient={onToggleRecipient}
          />
        ) : null}
      </div>
    </div>
  )
}

function NotificationRecipientDropdown({
  channelLabel,
  eventId,
  eventName,
  eventRecipientIds,
  getRecipientValue,
  isDisabled,
  recipients,
  onToggleRecipient,
}: NotificationRecipientDropdownProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghostMuted"
          size="icon-3xs"
          aria-label={`${eventName} ${channelLabel} recipients`}
          disabled={isDisabled}
        >
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" size="lg">
        <DropdownMenuLabel>{channelLabel} recipients</DropdownMenuLabel>
        <div className="grid gap-1 px-1 py-1">
          {recipients.map((recipient) => {
            const isSelected = eventRecipientIds.includes(recipient.id)
            const toggleSelectedRecipient = () => {
              if (isDisabled) {
                return
              }

              onToggleRecipient(eventId, recipient.id, !isSelected)
            }

            return (
              <SelectableSurface
                key={recipient.id}
                type="button"
                selected={isSelected}
                disabled={isDisabled}
                appearance="bare"
                padding="none"
                className="flex items-start gap-3 px-2 py-2 text-left hover:bg-accentSecondary data-[state=on]:bg-accentSecondary"
                onClick={toggleSelectedRecipient}
              >
                <Checkbox
                  checked={isSelected}
                  aria-label={`recipient-${recipient.id}-selected`}
                  className="pointer-events-none mt-0.5"
                  tabIndex={-1}
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium leading-5">
                    {recipient.label || getRecipientValue(recipient)}
                  </div>
                  {recipient.label ? (
                    <div className="text-xs text-muted-foreground">
                      {getRecipientValue(recipient)}
                    </div>
                  ) : null}
                </div>
              </SelectableSurface>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PrimaryLocationFallbackCell({
  canEdit,
  channelLabel,
  contactDescription,
  eventId,
  eventName,
  eventRecipientIds,
  getRecipientValue,
  isPending,
  recipients,
  showTopBorder,
  onToggleRecipient,
}: PrimaryLocationFallbackCellProps) {
  const showDropdown = recipients.length > 0

  return (
    <div className={cn('px-3 py-4', showTopBorder && 'border-t')}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn('flex items-center justify-center gap-1', !showDropdown && 'mt-1')}>
            <Checkbox
              checked
              disabled
              aria-label={`${eventName} ${channelLabel}`}
              className="pointer-events-none"
              tabIndex={-1}
            />
            {showDropdown ? (
              <NotificationRecipientDropdown
                channelLabel={channelLabel}
                eventId={eventId}
                eventName={eventName}
                eventRecipientIds={eventRecipientIds}
                getRecipientValue={getRecipientValue}
                isDisabled={!canEdit || isPending}
                recipients={recipients}
                onToggleRecipient={onToggleRecipient}
              />
            ) : null}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>
            {channelLabel} will use the {contactDescription}.
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  )
}
